import { useState } from 'react';
import { Link } from 'react-router-dom';

const Footer = () => {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const handleNewsletterSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');

    const form = e.currentTarget;
    const formData = new FormData(form);
    const email = formData.get('email') as string;

    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setFormStatus('error');
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/newsletter_subscription', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 3000);
      } else {
        setFormStatus('error');
        setTimeout(() => setFormStatus('idle'), 3000);
      }
    } catch (error) {
      setFormStatus('error');
      setTimeout(() => setFormStatus('idle'), 3000);
    }
  };

  return (
    <footer className="bg-gradient-to-br from-indigo-950 via-slate-950 to-purple-950 text-white relative overflow-hidden">
      <div className="absolute inset-0 opacity-5"></div>
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-12">
          <div className="lg:col-span-4">
            <div className="mb-6">
              <div className="text-2xl font-black tracking-tight">
                <span className="text-white">AZZ</span>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-500 to-purple-600"> MATRIX</span>
              </div>
            </div>
            <h3 className="text-3xl font-serif mb-6 leading-tight">Powering Your<br />Digital Success</h3>
            <form onSubmit={handleNewsletterSubmit} className="space-y-2" data-readdy-form id="newsletter_form">
              <div className="flex items-center space-x-2">
                <input 
                  type="email" 
                  name="email"
                  placeholder="Enter your email" 
                  className="flex-1 px-4 py-3 bg-transparent border border-violet-500/50 rounded-lg text-white placeholder-violet-200/50 focus:outline-none focus:border-violet-400 text-sm" 
                  required
                  maxLength={100}
                />
                <button 
                  type="submit"
                  disabled={formStatus === 'submitting'}
                  className="w-12 h-12 flex items-center justify-center bg-violet-600 rounded-full hover:bg-violet-700 transition-colors cursor-pointer disabled:opacity-50"
                >
                  {formStatus === 'submitting' ? (
                    <i className="ri-loader-4-line animate-spin text-white text-lg"></i>
                  ) : (
                    <i className="ri-notification-line text-white text-lg"></i>
                  )}
                </button>
              </div>
              {formStatus === 'success' && (
                <p className="text-xs text-green-300">✓ Subscribed successfully!</p>
              )}
              {formStatus === 'error' && (
                <p className="text-xs text-red-300">✗ Please enter a valid email</p>
              )}
            </form>
          </div>

          <div className="lg:col-span-8 grid grid-cols-2 md:grid-cols-4 gap-8">
            <div>
              <h4 className="text-lg font-bold mb-4">Services</h4>
              <ul className="space-y-2">
                <li><Link to="/services" className="text-sm text-violet-100 hover:text-violet-300 transition-colors">IT Support</Link></li>
                <li><Link to="/services" className="text-sm text-violet-100 hover:text-violet-300 transition-colors">Cloud Solutions</Link></li>
                <li><Link to="/services" className="text-sm text-violet-100 hover:text-violet-300 transition-colors">Cybersecurity</Link></li>
                <li><Link to="/services" className="text-sm text-violet-100 hover:text-violet-300 transition-colors">IT Training</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-4">Solutions</h4>
              <ul className="space-y-2">
                <li><Link to="/services" className="text-sm text-violet-100 hover:text-violet-300 transition-colors">Enterprise</Link></li>
                <li><Link to="/services" className="text-sm text-violet-100 hover:text-violet-300 transition-colors">SMB</Link></li>
                <li><Link to="/services" className="text-sm text-violet-100 hover:text-violet-300 transition-colors">Startup</Link></li>
                <li><Link to="/services" className="text-sm text-violet-100 hover:text-violet-300 transition-colors">Government</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-4">Company</h4>
              <ul className="space-y-2">
                <li><Link to="/" className="text-sm text-violet-100 hover:text-violet-300 transition-colors">About</Link></li>
                <li><Link to="/" className="text-sm text-violet-100 hover:text-violet-300 transition-colors">Careers</Link></li>
                <li><Link to="/" className="text-sm text-violet-100 hover:text-violet-300 transition-colors">Partners</Link></li>
                <li><Link to="/" className="text-sm text-violet-100 hover:text-violet-300 transition-colors">News</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-4">Resources</h4>
              <ul className="space-y-2">
                <li><Link to="/" className="text-sm text-violet-100 hover:text-violet-300 transition-colors">Blog</Link></li>
                <li><Link to="/" className="text-sm text-violet-100 hover:text-violet-300 transition-colors">Documentation</Link></li>
                <li><Link to="/contact" className="text-sm text-violet-100 hover:text-violet-300 transition-colors">Support</Link></li>
                <li><Link to="/" className="text-sm text-violet-100 hover:text-violet-300 transition-colors">Community</Link></li>
              </ul>
            </div>
          </div>
        </div>

        <div className="pt-8 border-t border-purple-800/50">
          {/* Credit Card Logos */}
          <div className="flex items-center justify-center flex-wrap gap-3 mb-6">
            <span className="text-xs text-violet-300 mr-2 font-medium tracking-wide uppercase">We Accept</span>
            {[
              { src: 'https://cdn.jsdelivr.net/npm/payment-icons@1.1.0/min/flat/visa.svg', alt: 'Visa' },
              { src: 'https://cdn.jsdelivr.net/npm/payment-icons@1.1.0/min/flat/mastercard.svg', alt: 'Mastercard' },
              { src: 'https://cdn.jsdelivr.net/npm/payment-icons@1.1.0/min/flat/amex.svg', alt: 'American Express' },
              { src: 'https://cdn.jsdelivr.net/npm/payment-icons@1.1.0/min/flat/discover.svg', alt: 'Discover' },
              { src: 'https://cdn.jsdelivr.net/npm/payment-icons@1.1.0/min/flat/paypal.svg', alt: 'PayPal' },
            ].map((card) => (
              <div
                key={card.alt}
                className="w-12 h-8 bg-white rounded-md flex items-center justify-center p-1 opacity-90 hover:opacity-100 transition-opacity"
                title={card.alt}
              >
                <img src={card.src} alt={card.alt} className="w-full h-full object-contain" />
              </div>
            ))}
          </div>

          <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
            <p className="text-sm text-violet-200">© 2025 AZZ MATRIX. All rights reserved.</p>
            
            <div className="flex items-center space-x-5">
              <a 
                href="https://linkedin.com" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="w-10 h-10 flex items-center justify-center border border-violet-500/50 rounded-lg hover:bg-violet-600/20 hover:border-violet-400 transition-all cursor-pointer"
              >
                <i className="ri-linkedin-fill text-lg"></i>
              </a>
              <a 
                href="https://twitter.com" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="w-10 h-10 flex items-center justify-center border border-violet-500/50 rounded-lg hover:bg-violet-600/20 hover:border-violet-400 transition-all cursor-pointer"
              >
                <i className="ri-twitter-x-fill text-lg"></i>
              </a>
              <a 
                href="https://github.com" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="w-10 h-10 flex items-center justify-center border border-violet-500/50 rounded-lg hover:bg-violet-600/20 hover:border-violet-400 transition-all cursor-pointer"
              >
                <i className="ri-github-fill text-lg"></i>
              </a>
              <a 
                href="https://youtube.com" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="w-10 h-10 flex items-center justify-center border border-violet-500/50 rounded-lg hover:bg-violet-600/20 hover:border-violet-400 transition-all cursor-pointer"
              >
                <i className="ri-youtube-fill text-lg"></i>
              </a>
            </div>

            <div className="flex items-center space-x-4 text-sm text-violet-200">
              <Link to="/legal#refund-policy" className="hover:text-violet-300 transition-colors whitespace-nowrap">Refund Policy</Link>
              <span>|</span>
              <Link to="/legal#privacy" className="hover:text-violet-300 transition-colors whitespace-nowrap">Privacy Policy</Link>
              <span>|</span>
              <Link to="/legal#terms" className="hover:text-violet-300 transition-colors whitespace-nowrap">Terms of Service</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;